import React from 'react'

import { AnalyticsClientProvider } from '@acorns/web-utils'
import { createInstance as createOptimizelyInstance } from '@optimizely/react-sdk'
import { compose, mapProps, nest } from 'recompose'
import { ThemeProvider } from 'styled-components'

import AccessibilityAnnouncement from 'components/AccessibilityAnnouncements'
import Messages from 'components/AccessibilityAnnouncements/messageTemplates'

import { Provider as ApolloProvider } from './config/apollo'
import { GlobalFontStyles } from './config/fonts'
import { NormalizeStyles } from './config/normalize'
import { GlobalStyles } from './config/styles'
import Router from './router'
import Environment, { EnvironmentProvider } from './utils/environment'
import { Provider as ApolloErrorHandlingProvider } from './utils/error'
import { Provider as OptimizelyProvider, eventProxy } from './utils/experiments'
import { setUdid } from './utils/udid-utils'
import withLogVersion from './utils/with-log-version'

type Props = {
  appId: string
  env: Environment
  graphqlUrl: string
  theme: any
  message?: string
}

const Providers = nest(
  EnvironmentProvider,
  ThemeProvider,
  ApolloErrorHandlingProvider,
  ApolloProvider,
)

const enhance = compose<Props, Props>(
  withLogVersion,
  mapProps((props: Props) => ({
    appId: props.appId,
    env: props.env,
    graphqlUrl: props.env.get('graphql'),
    theme: props.theme,
  })),
)

const Application = (props: Props) => {
  setUdid()

  const optimizely = createOptimizelyInstance({
    sdkKey: props.env.get('optimizelySdkKey'),
    datafileOptions: {
      urlTemplate: `${props.env.get('frontendProxyHost')}/opt?s=%s`,
    },
    eventDispatcher: eventProxy,
  })

  return (
    <Providers {...props} key={'app'}>
      <AnalyticsClientProvider
        rudderStack={{
          dataPlaneUrl: props.env.get('rudderStackDataPlaneUrl'),
          writeKey: props.env.get('rudderStackWriteKey'),
        }}
      >
        <OptimizelyProvider optimizely={optimizely}>
          <AccessibilityAnnouncement message={Messages.welcome()} />
          <NormalizeStyles />
          <GlobalStyles />
          <GlobalFontStyles />
          <Router />
        </OptimizelyProvider>
      </AnalyticsClientProvider>
    </Providers>
  )
}

export default enhance(Application)
